<template>
  <div class="w-screen min-h-screen overflow-y-auto overflow-x-hidden bg-gray-50 relative pb-20">
    <div class="w-full min-h-screen overflow-y-auto overflow-x-hidden relative">
      <!-- Header -->
      <div
        class="w-full bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center print:hidden shadow-sm">
        <div class="font-bold text-blue-600">E-TRANSAKSI</div>
        <svg @click="logout" xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 stroke-current text-red-500 cursor-pointer hover:text-red-700 transition" fill="none"
          viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </div>

      <!-- Content -->
      <div class="p-4 max-w-7xl mx-auto">
        <!-- Title -->
        <div class="text-center mb-6">
          <h5 class="font-bold text-blue-700 text-lg">UPAH PASANG</h5>
          <h6 class="font-bold text-red-500">EF-PRINT</h6>
        </div>

        <!-- Data Table -->
        <div class="mb-4 overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
          <table class="w-full text-sm text-left">
            <thead class="bg-gray-100 text-gray-700 uppercase text-xs">
              <tr>
                <th class="px-4 py-3 text-center font-bold whitespace-nowrap border-b w-16">NO</th>
                <th class="px-4 py-3 font-bold whitespace-nowrap border-b">NAMA</th>
                <th class="px-4 py-3 text-right font-bold whitespace-nowrap border-b">TOTAL UPAH</th>
                <th class="px-4 py-3 text-right font-bold whitespace-nowrap border-b">PEMBAYARAN</th>
                <th class="px-4 py-3 text-right font-bold border-b">SISA PEMBAYARAN</th>
                <th class="px-4 py-3 text-center font-bold border-b w-36">AKSI</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="upahData.length === 0 && !isLoading">
                <td colspan="6" class="px-4 py-8 text-center text-gray-500 italic border-b">
                  Belum ada data upah pasang
                </td>
              </tr>
              <tr v-if="isLoading">
                <td colspan="6" class="px-4 py-8 text-center text-gray-500 border-b">
                  Loading data...
                </td>
              </tr>
              <template v-for="(item, index) in upahData" :key="item.id_user">
                <tr class="border-b hover:bg-blue-50 transition duration-150">
                  <td class="px-4 py-3 text-center text-gray-800">{{ index + 1 }}</td>
                  <td class="px-4 py-3 font-semibold text-gray-800">{{ item.nama }}</td>
                  <td class="px-4 py-3 text-right font-semibold text-blue-600">{{ formatRupiah(item.total_upah) }}</td>
                  <td class="px-4 py-3 text-right font-semibold text-green-600">{{ formatRupiah(item.pembayaran) }}</td>
                  <td class="px-4 py-3 text-right font-bold"
                    :class="item.sisa_pembayaran > 0 ? 'text-red-500' : 'text-green-600'">
                    {{ formatRupiah(item.sisa_pembayaran) }}
                  </td>
                  <td class="px-4 py-3 text-center whitespace-nowrap space-x-1">
                    <!-- Bayar -->
                    <button @click="showPaymentModal(item)"
                      class="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-2.5 py-1.5 rounded transition">
                      Bayar
                    </button>
                    <!-- History -->
                    <button @click="showHistory(item)"
                      class="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-2.5 py-1.5 rounded border border-gray-300 transition">
                      History
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <!-- ===== GRAND TOTAL ===== -->
            <tfoot v-if="upahData.length > 0 && !isLoading">
              <tr class="bg-blue-700 text-white">
                <td colspan="2" class="px-4 py-3 font-bold text-sm uppercase tracking-wide">Grand Total</td>
                <td class="px-4 py-3 text-right font-bold text-sm whitespace-nowrap">
                  {{ formatRupiah(grandTotalUpah) }}
                </td>
                <td class="px-4 py-3 text-right font-bold text-sm whitespace-nowrap">
                  {{ formatRupiah(grandTotalPembayaran) }}
                </td>
                <td class="px-4 py-3 text-right font-bold text-sm whitespace-nowrap"
                  :class="grandTotalSisa > 0 ? 'text-yellow-300' : 'text-green-300'">
                  {{ formatRupiah(grandTotalSisa) }}
                  <span v-if="grandTotalSisa === 0" class="ml-1 text-xs">✓ Lunas</span>
                </td>
                <td class="px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- ===== MODAL PEMBAYARAN ===== -->
      <div v-if="paymentModal.show"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 class="font-bold text-gray-700">Bayar Upah</h3>
            <button @click="paymentModal.show = false" class="text-gray-400 hover:text-gray-600">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="p-4">
            <div class="mb-3">
              <label class="block text-xs font-bold text-gray-600 mb-1">Personil</label>
              <div class="text-sm font-semibold text-gray-800">{{ paymentModal.data?.nama }}</div>
            </div>
            <div class="mb-3 flex gap-2">
              <div class="flex-1">
                <label class="block text-xs font-bold text-gray-600 mb-1">Total Upah</label>
                <div class="text-xs font-semibold text-blue-600">{{ formatRupiah(paymentModal.data?.total_upah) }}</div>
              </div>
              <div class="flex-1">
                <label class="block text-xs font-bold text-gray-600 mb-1">Sisa Pembayaran</label>
                <div class="text-xs font-bold text-red-500">{{ formatRupiah(paymentModal.data?.sisa_pembayaran) }}</div>
              </div>
            </div>
            <!-- Tanggal -->
            <div class="mb-3">
              <label class="block text-xs font-bold text-gray-600 mb-1">Tanggal Pembayaran</label>
              <input type="date" v-model="paymentModal.tanggal"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
            </div>
            <!-- Nominal -->
            <div class="mb-3">
              <label class="block text-xs font-bold text-gray-600 mb-1">Nominal Bayar (Rp)</label>
              <input type="text" :value="getDisplayValue(paymentModal.amount, paymentModal.focused)"
                @focus="paymentModal.focused = true" @blur="paymentModal.focused = false"
                @input="paymentModal.amount = unformatNumber($event.target.value)"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 text-right font-semibold"
                placeholder="0">
            </div>
            <!-- Tipe Transaksi -->
            <div class="mb-4">
              <label class="block text-xs font-bold text-gray-600 mb-1">Tipe Transaksi</label>
              <div class="flex gap-3">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="paymentModal.tipe_transaksi" value="cash" class="accent-blue-600">
                  <span class="text-sm font-semibold text-gray-700">💵 Cash</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="paymentModal.tipe_transaksi" value="transfer" class="accent-blue-600">
                  <span class="text-sm font-semibold text-gray-700">🏦 Transfer</span>
                </label>
              </div>
            </div>
            <!-- Keterangan -->
            <div class="mb-4">
              <label class="block text-xs font-bold text-gray-600 mb-1">Keterangan (opsional)</label>
              <input type="text" v-model="paymentModal.keterangan" placeholder="Contoh: Transfer BCA"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
            </div>
            <div class="flex justify-end gap-2">
              <button @click="paymentModal.show = false"
                class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-bold rounded transition">Batal</button>
              <button @click="submitPayment" :disabled="isSubmitting"
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded transition disabled:opacity-50">
                {{ isSubmitting ? 'Menyimpan...' : 'Simpan' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== MODAL HISTORY PEMBAYARAN ===== -->
      <div v-if="historyModal.show"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <div>
              <h3 class="font-bold text-gray-700">History Pembayaran</h3>
              <p class="text-xs text-gray-500">{{ historyModal.nama }}</p>
            </div>
            <button @click="historyModal.show = false" class="text-gray-400 hover:text-gray-600">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="p-4 max-h-96 overflow-y-auto">
            <div v-if="historyModal.isLoading" class="text-center py-6 text-blue-600 text-sm animate-pulse">
              Loading...
            </div>
            <div v-else-if="historyModal.list.length === 0" class="text-center py-6 text-gray-400 italic text-sm">
              Belum ada history pembayaran
            </div>
            <table v-else class="w-full text-sm">
              <thead class="bg-gray-100 text-gray-600 uppercase text-xs">
                <tr>
                  <th class="px-3 py-2 text-center font-bold">Tanggal</th>
                  <th class="px-3 py-2 text-right font-bold">Nominal</th>
                  <th class="px-3 py-2 text-center font-bold">Tipe</th>
                  <th class="px-3 py-2 font-bold">Keterangan</th>
                  <th class="px-3 py-2 text-center font-bold w-12">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in historyModal.list" :key="h.id" class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-3 py-2 text-center whitespace-nowrap text-gray-700">{{ formatDate(h.tanggal) }}</td>
                  <td class="px-3 py-2 text-right font-semibold text-green-600 whitespace-nowrap">{{ formatRupiah(h.nominal) }}</td>
                  <td class="px-3 py-2 text-center whitespace-nowrap">
                    <span :class="h.tipe_transaksi === 'cash'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-blue-100 text-blue-700'"
                      class="px-2 py-0.5 rounded text-[10px] font-bold uppercase whitespace-nowrap">
                      {{ h.tipe_transaksi === 'cash' ? '💵 Cash' : '🏦 Transfer' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{{ h.keterangan || '-' }}</td>
                  <td class="px-3 py-2 text-center whitespace-nowrap">
                    <button @click="deleteHistory(h.id)" class="text-red-500 hover:text-red-700" title="Hapus">🗑️</button>
                  </td>
                </tr>
                <!-- Total -->
                <tr class="bg-green-50 font-bold">
                  <td colspan="1" class="px-3 py-2 text-xs text-gray-600 uppercase whitespace-nowrap">Total</td>
                  <td class="px-3 py-2 text-right text-green-700 whitespace-nowrap">{{ formatRupiah(totalHistory) }}</td>
                  <td colspan="3" class="whitespace-nowrap"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <bottom-nav></bottom-nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import BottomNav from '../components/BottomNav.vue'

export default {
  name: 'UpahPasang',
  components: { BottomNav },
  data() {
    return {
      isLoading: false,
      upahData: [],
      paymentModal: {
        show: false,
        focused: false,
        data: null,
        amount: 0,
        tanggal: new Date().toISOString().slice(0, 10),
        tipe_transaksi: 'cash',
        keterangan: ''
      },
      historyModal: {
        show: false,
        isLoading: false,
        id_user: null,
        nama: '',
        list: []
      },
      isSubmitting: false
    }
  },
  computed: {
    totalHistory() {
      return this.historyModal.list.reduce((sum, h) => sum + parseFloat(h.nominal || 0), 0)
    },
    grandTotalUpah() {
      return this.upahData.reduce((sum, d) => sum + (parseFloat(d.total_upah) || 0), 0)
    },
    grandTotalPembayaran() {
      return this.upahData.reduce((sum, d) => sum + (parseFloat(d.pembayaran) || 0), 0)
    },
    grandTotalSisa() {
      return this.upahData.reduce((sum, d) => sum + (parseFloat(d.sisa_pembayaran) || 0), 0)
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.isLoading = true
      try {
        const response = await axios.get('/e_transaksi/job_sheet_upah/get_all_upah_pasang', {
          params: { token: this.$store.state.token }
        })
        if (response.data.status) {
          this.upahData = response.data.data
        }
      } catch (error) {
        console.error('Failed to fetch upah pasang:', error)
      } finally {
        this.isLoading = false
      }
    },

    showPaymentModal(item) {
      this.paymentModal.data = item
      this.paymentModal.amount = 0
      this.paymentModal.tanggal = new Date().toISOString().slice(0, 10)
      this.paymentModal.tipe_transaksi = 'cash'
      this.paymentModal.keterangan = ''
      this.paymentModal.focused = false
      this.paymentModal.show = true
    },

    async submitPayment() {
      if (!this.paymentModal.amount || this.paymentModal.amount <= 0) {
        alert('Nominal pembayaran tidak valid')
        return
      }
      this.isSubmitting = true
      try {
        const payload = {
          id_user: this.paymentModal.data.id_user,
          nominal: this.paymentModal.amount,
          tipe_transaksi: this.paymentModal.tipe_transaksi,
          tanggal: this.paymentModal.tanggal,
          keterangan: this.paymentModal.keterangan || null
        }
        const response = await axios.post('/e_transaksi/job_sheet_upah/pay_upah_personil', payload, {
          params: { token: this.$store.state.token }
        })
        if (response.data.status) {
          alert('Pembayaran berhasil disimpan')
          this.paymentModal.show = false
          this.fetchData()
        } else {
          alert('Gagal: ' + response.data.message)
        }
      } catch (error) {
        console.error('Failed to submit payment:', error)
        alert('Terjadi kesalahan saat menyimpan pembayaran')
      } finally {
        this.isSubmitting = false
      }
    },

    async showHistory(item) {
      this.historyModal.id_user = item.id_user
      this.historyModal.nama = item.nama
      this.historyModal.list = []
      this.historyModal.isLoading = true
      this.historyModal.show = true
      try {
        const response = await axios.get(`/e_transaksi/job_sheet_upah/get_history/${item.id_user}`, {
          params: { token: this.$store.state.token }
        })
        if (response.data.status) {
          this.historyModal.list = response.data.data
        }
      } catch (error) {
        console.error('Failed to fetch history:', error)
      } finally {
        this.historyModal.isLoading = false
      }
    },

    async deleteHistory(paymentId) {
      if (!confirm('Hapus data pembayaran ini?')) return
      try {
        const response = await axios.post(`/e_transaksi/job_sheet_upah/delete_payment/${paymentId}`, null, {
          params: { token: this.$store.state.token }
        })
        if (response.data.status) {
          this.historyModal.list = this.historyModal.list.filter(h => h.id !== paymentId)
          this.fetchData()
        } else {
          alert('Gagal: ' + response.data.message)
        }
      } catch (error) {
        console.error('Failed to delete payment:', error)
        alert('Terjadi kesalahan saat menghapus.')
      }
    },

    formatRupiah(angka) {
      if (!angka && angka !== 0) return '0'
      return new Intl.NumberFormat('id-ID').format(angka)
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    getDisplayValue(val, isFocused) {
      const num = Number(val) || 0
      if (isFocused) {
        return num === 0 ? '' : num
      }
      return num === 0 ? '' : this.formatRupiah(num)
    },

    unformatNumber(val) {
      if (!val) return 0
      const num = val.toString().replace(/[^0-9]/g, '')
      return parseInt(num, 10) || 0
    },

    logout() {
      this.$store.dispatch('set_token', null)
      this.$store.dispatch('set_user', null)
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>

<style scoped></style>
